import { PropsWithChildren, useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { isVersionLower } from 'utils/version.ts'
import { APP_STORE_URLS, NativePlatform } from 'constants/appVersion.ts'
import { log } from 'utils/logging.ts'
import { App as CapacitorApp } from '@capacitor/app'
import { ForcedModal, Button, Heading } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import Logo from 'assets/Logo.tsx'
import styles from './AppVersionProvider.module.scss'
import LanguageSwitchButton from 'components/language-switch-button/LanguageSwitchButton.tsx'
import { useGetMinimumAppVersionQuery } from 'store/queries/cms-api'

const AppVersionProvider = ({ children }: PropsWithChildren) => {
  // i18n
  const { t } = useTranslation()

  // Local state
  const [showForceModal, setShowForceModal] = useState<NativePlatform | undefined>()

  // Constants
  const isNativePlatform = Capacitor.isNativePlatform()

  // Redux Query
  const { data } = useGetMinimumAppVersionQuery(null, { skip: !isNativePlatform })

  // Enforces the minimum app version for native platforms (iOS and Android).
  useEffect(() => {
    const enforceMinimumAppVersion = async () => {
      if (!isNativePlatform || !data) return

      const platform = Capacitor.getPlatform()
      if (platform !== 'ios' && platform !== 'android') return

      try {
        const { version } = await CapacitorApp.getInfo()

        if (isVersionLower(version, data[platform])) {
          setShowForceModal(platform)
        }
      } catch (error) {
        log({ error: `Failed to check app version for ${platform}`, identifier: '[APP]' })
      }
    }

    // Force a native app update if the installed version is no longer supported
    void enforceMinimumAppVersion()
  }, [data, isNativePlatform])

  return showForceModal ? (
    <>
      <div className={styles.placeholder}>
        <Logo className={styles.logo} />
      </div>
      {showForceModal && (
        <ForcedModal
          isOpen
          title={
            <div className={styles['title-wrapper']}>
              <Heading as="h1" variant="h5">
                {t(($) => $.forceUpdate.title)}
              </Heading>

              <LanguageSwitchButton />
            </div>
          }
        >
          <p>{t(($) => $.forceUpdate.body)}</p>
          <Button onClick={() => window.open(APP_STORE_URLS[showForceModal], '_system')}>{t(($) => $.forceUpdate.cta)}</Button>
        </ForcedModal>
      )}
    </>
  ) : (
    children
  )
}

export default AppVersionProvider
